import React, { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import './ManageClinics.css';
import BackToClinicDashboard from '../BackToClinicDashboard';
import { useParams } from 'react-router-dom';


const ClinicVisitsView = () => {
  const { user } = useUser();
  const { t } = useTranslation();
  const [visits, setVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchName, setSearchName] = useState('');
  const [selectedClinic, setSelectedClinic] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const { token } = useParams();

  useEffect(() => {
    const fetchData = async () => {
      if (!user || user.role !== 'system_admin') return;

      try {
        const clinicsSnap = await getDocs(collection(db, 'clinics'));
        const clinicsList = clinicsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setClinics(clinicsList);

        const visitsSnap = await getDocs(collection(db, 'clinic_visits'));
        const enriched = await Promise.all(
          visitsSnap.docs.map(async (docSnap) => {
            const data = docSnap.data();
            let customerName = 'Unknown';

            if (data.customer_token) {
              const q = query(collection(db, 'customers'), where('token', '==', data.customer_token));
              const snap = await getDocs(q);
              if (!snap.empty) {
                customerName = snap.docs[0].data().name;
              }
            }

            return {
              id: docSnap.id,
              ...data,
              customer_name: customerName,
            };
          })
        );

        setVisits(enriched);
        setFilteredVisits(enriched);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [user]);

  useEffect(() => {
    let results = visits;
    if (searchName) {
      results = results.filter(v => v.customer_name?.toLowerCase().includes(searchName.toLowerCase()));
    }
    if (selectedClinic) {
      results = results.filter(v => v.clinic_id === selectedClinic);
    }
    if (selectedDate) {
      results = results.filter(v => v.visited_at?.toDate?.().toISOString().slice(0, 10) === selectedDate);
    }
    setFilteredVisits(results);
  }, [searchName, selectedClinic, selectedDate, visits]);

  const exportCSV = () => {
    const headers = ['Customer', 'Clinic', 'Date', 'Confirmed By'];
    const rows = filteredVisits.map(v => [
      v.customer_name,
      v.clinic_name,
      v.visited_at?.toDate?.().toLocaleString?.() || '',
      v.confirmed_by_email || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'clinic_visits.csv';
    link.click();
  };

  if (!user || user.role !== 'system_admin') return null;

  return (
    <div className="manage-clinics-container">
        <BackToClinicDashboard token={token} />
      <h2 className="manage-clinics-title">{t('clinicVisits.title')}</h2>

      <div className="form-grid">
        <input
          type="text"
          placeholder={t('clinicVisits.searchName')}
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
        />
        <select value={selectedClinic} onChange={(e) => setSelectedClinic(e.target.value)}>
          <option value="">{t('clinicVisits.selectClinic')}</option>
          {clinics.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
        <button className="clinic-action-btn export-btn" onClick={exportCSV}>
          {t('clinicVisits.exportCsv')}
        </button>
      </div>

      <p className="text-gray-600 font-medium my-2">
        {t('clinicVisits.total')}: {filteredVisits.length}
      </p>

      {loading ? (
        <p>{t('loading')}...</p>
      ) : filteredVisits.length === 0 ? (
        <p>{t('clinicVisits.noVisits')}</p>
      ) : (
        <div className="clinic-list">
          {filteredVisits.map(v => (
            <div key={v.id} className="clinic-card">
              <div className="clinic-card-info">
                <p><strong>{t('clinicVisits.customer')}:</strong> {v.customer_name}</p>
                <p><strong>{t('clinicVisits.clinic')}:</strong> {v.clinic_name}</p>
                <p><strong>{t('clinicVisits.date')}:</strong> {v.visited_at?.toDate?.().toLocaleString?.()}</p>
                <p><strong>{t('clinicVisits.confirmedBy')}:</strong> {v.confirmed_by_email || 'N/A'}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClinicVisitsView;
